/**
 * DatePicker.tsx — small inline date input with consistent styling.
 *
 * Wraps the native `<input type="date">` so every page reads consistent.
 * For multi-snapshot pages we offer a `<select>` of available dates instead
 * (so users can't pick a date that has no data).
 */

import React from 'react';

interface Props {
	value: string | null;
	onChange: (next: string) => void;
	availableDates?: string[];
	label?: string;
}

export default function DatePicker({ value, onChange, availableDates, label = 'Date' }: Props) {
	const opts = Array.isArray(availableDates) ? availableDates : [];

	if (opts.length > 0) {
		return (
			<label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
				<span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-faint)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
					{label}
				</span>
				<select
					value={value ?? ''}
					onChange={(e) => onChange(e.target.value)}
					className="select"
					style={{ paddingTop: 6, paddingBottom: 6 }}
				>
					{opts.map((d) => (
						<option key={d} value={d}>
							{d}
						</option>
					))}
				</select>
			</label>
		);
	}

	return (
		<label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
			<span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-faint)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
				{label}
			</span>
			<input
				type="date"
				value={value ?? ''}
				onChange={(e) => onChange(e.target.value)}
				style={{
					padding: '6px 10px',
					border: '1px solid var(--border)',
					borderRadius: 'var(--radius-sm)',
					background: 'var(--surface)',
					fontSize: 12.5,
					color: 'var(--text)',
				}}
			/>
		</label>
	);
}
